/**
 * Folder Picker Component
 *
 * Modal component for browsing and selecting a Google Drive folder.
 * Used for selecting the data room folder to connect.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, Pressable, Modal, FlatList, ActivityIndicator } from 'react-native';
import { Folder, ChevronRight, ChevronLeft, Check, X } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useGoogleDriveStore } from '@/lib/google-drive';
import type { GoogleDriveFolder } from '@/lib/google-drive';
import AnimatedPressable from '@/components/AnimatedPressable';
import { isWeb } from '@/lib/platform';

interface FolderPickerProps {
  visible: boolean;
  onClose: () => void;
  onSelectFolder: (folder: GoogleDriveFolder) => void;
  title?: string;
}

export function FolderPicker({
  visible,
  onClose,
  onSelectFolder,
  title = 'Select Data Room Folder',
}: FolderPickerProps) {
  const colors = useThemeColors();
  const [currentPath, setCurrentPath] = useState<GoogleDriveFolder[]>([
    { id: 'root', name: 'My Drive' },
  ]);

  const {
    folders,
    isLoadingFiles,
    loadFilesAndFolders,
    error,
  } = useGoogleDriveStore();

  const currentFolderId = currentPath[currentPath.length - 1]?.id || 'root';

  // Load folders when path changes
  useEffect(() => {
    if (visible) {
      loadFilesAndFolders(currentFolderId);
    }
  }, [visible, currentFolderId, loadFilesAndFolders]);

  // Reset path when modal closes
  useEffect(() => {
    if (!visible) {
      setCurrentPath([{ id: 'root', name: 'My Drive' }]);
    }
  }, [visible]);

  const handleFolderPress = (folder: GoogleDriveFolder) => {
    setCurrentPath([...currentPath, folder]);
  };

  const handleBack = () => {
    if (currentPath.length > 1) {
      setCurrentPath(currentPath.slice(0, -1));
    }
  };

  const handleSelect = () => {
    const selectedFolder = currentPath[currentPath.length - 1];
    onSelectFolder(selectedFolder);
    onClose();
  };

  const renderFolder = ({ item, index }: { item: GoogleDriveFolder; index: number }) => (
    <Animated.View entering={FadeInDown.delay(index * 50).duration(300)}>
      <AnimatedPressable
        onPress={() => handleFolderPress(item)}
        className="flex-row items-center p-4 border-b"
        style={{ borderColor: colors.border.light }}
      >
        <View
          className="w-10 h-10 rounded-xl items-center justify-center mr-3"
          style={{ backgroundColor: `${colors.warning}20` }}
        >
          <Folder size={20} color={colors.warning} />
        </View>
        <Text
          style={{ color: colors.text.primary }}
          className="flex-1 font-medium"
          numberOfLines={1}
        >
          {item.name}
        </Text>
        <ChevronRight size={20} color={colors.text.tertiary} />
      </AnimatedPressable>
    </Animated.View>
  );

  return (
    <Modal
      visible={visible}
      animationType={isWeb ? 'fade' : 'slide'}
      presentationStyle={isWeb ? undefined : 'pageSheet'}
      transparent={isWeb}
      onRequestClose={onClose}
    >
      <View style={isWeb ? { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' } : { flex: 1 }}>
      <View className={isWeb ? '' : 'flex-1'} style={{ backgroundColor: colors.surface.secondary, ...(isWeb && { width: '100%', maxWidth: 520, maxHeight: '85%', borderRadius: 16, overflow: 'hidden' }) }}>
        {/* Header */}
        <View
          className="flex-row items-center justify-between p-4 border-b"
          style={{ borderColor: colors.border.light, backgroundColor: colors.surface.primary }}
        >
          <AnimatedPressable onPress={onClose} className="p-2">
            <X size={24} color={colors.text.primary} />
          </AnimatedPressable>
          <Text style={{ color: colors.text.primary }} className="font-semibold text-lg">
            {title}
          </Text>
          <AnimatedPressable onPress={handleSelect} className="p-2">
            <Check size={24} color={colors.primary[600]} />
          </AnimatedPressable>
        </View>

        {/* Breadcrumb */}
        <View
          className="flex-row items-center p-3 border-b"
          style={{ borderColor: colors.border.light, backgroundColor: colors.surface.primary }}
        >
          {currentPath.length > 1 && (
            <AnimatedPressable onPress={handleBack} className="mr-2 p-1">
              <ChevronLeft size={20} color={colors.primary[600]} />
            </AnimatedPressable>
          )}
          <Text style={{ color: colors.text.secondary }} className="text-sm flex-1" numberOfLines={1}>
            {currentPath.map((f) => f.name).join(' / ')}
          </Text>
        </View>

        {/* Current folder selection indicator */}
        <Animated.View
          entering={FadeIn}
          className="p-4 border-b"
          style={{ borderColor: colors.border.light, backgroundColor: colors.primary[50] }}
        >
          <View className="flex-row items-center">
            <View
              className="w-10 h-10 rounded-xl items-center justify-center mr-3"
              style={{ backgroundColor: colors.primary[100] }}
            >
              <Folder size={20} color={colors.primary[600]} />
            </View>
            <View className="flex-1">
              <Text style={{ color: colors.primary[700] }} className="font-semibold">
                {currentPath[currentPath.length - 1]?.name || 'My Drive'}
              </Text>
              <Text style={{ color: colors.primary[600] }} className="text-xs">
                Tap the checkmark to select this folder as your data room
              </Text>
            </View>
          </View>
        </Animated.View>

        {/* Folder list */}
        {isLoadingFiles ? (
          <View className="flex-1 items-center justify-center">
            <ActivityIndicator size="large" color={colors.primary[600]} />
            <Text style={{ color: colors.text.tertiary }} className="mt-4">
              Loading folders...
            </Text>
          </View>
        ) : error ? (
          <View className="flex-1 items-center justify-center p-6">
            <Text style={{ color: colors.error }} className="text-center">
              {error}
            </Text>
          </View>
        ) : folders.length === 0 ? (
          <View className="flex-1 items-center justify-center p-6">
            <Folder size={48} color={colors.text.tertiary} />
            <Text style={{ color: colors.text.tertiary }} className="mt-4 text-center">
              No subfolders in this folder
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-sm text-center mt-1">
              Tap the checkmark to use this folder
            </Text>
          </View>
        ) : (
          <FlatList
            data={folders}
            keyExtractor={(item) => item.id}
            renderItem={renderFolder}
            className="flex-1"
            style={{ backgroundColor: colors.surface.primary }}
          />
        )}
      </View>
      </View>
    </Modal>
  );
}
